import React, { useEffect, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import Collapse from "@material-ui/core/Collapse";
import ExpandMore from "@material-ui/icons/ExpandMore";
import ChevronRight from "@material-ui/icons/ChevronRight";
import theme from "../../theme";
import history from "../../history";

const useStyles = makeStyles(() => ({
  root: {
    width: "230px",
    backgroundColor: theme.colors.Zumthor,
    height: "calc(100vh - 145px)",
    padding: "20px 0px",
    position: "fixed",
    zIndex: 1200,
    overflowY: "auto",
    overflowX: "hidden",
    borderTopRightRadius: "12px",
    borderBottomRightRadius: "12px",
    boxShadow: "0px 8px 24px rgba(62, 131, 250, 0.12)",
    scrollbarWidth: "thin",
    scrollbarColor: `${theme.colors.DodgerBlueLight} transparent`,
    "&::-webkit-scrollbar": {
      width: "8px",
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "rgba(62, 131, 250, 0.45)",
      borderRadius: "999px",
      border: "2px solid transparent",
      backgroundClip: "content-box",
    },
  },
  list: {
    padding: "8px 0",
  },
  tab: {
    margin: "4px 12px",
    borderRadius: "12px",
    paddingLeft: "20px",
    transition: "all 0.2s ease",
    color: theme.colors.DodgerBlueLight,
    "&:hover": {
      backgroundColor: "rgba(62, 131, 250, 0.12)",
    },
    "&.Mui-selected": {
      backgroundColor: theme.colors.DodgerBlueLight,
      color: theme.colors.White,
      boxShadow: "0px 6px 16px rgba(62, 131, 250, 0.25)",
    },
    "& .MuiListItemIcon-root": { minWidth: "35px" },
    "& .MuiListItemText-primary": {
      fontFamily: theme.fontFamily.MARKPROMEDIUM,
      fontSize: "15px",
    },
  },
  // Group header (a clickable parent that has children). Same shape as a tab
  // but never paints the active background — that signal belongs to the active
  // child. The caret on the right communicates open/closed.
  groupHeader: {
    margin: "4px 12px",
    borderRadius: "12px",
    paddingLeft: "20px",
    paddingRight: "12px",
    transition: "background-color 0.15s ease",
    color: theme.colors.DodgerBlueLight,
    "&:hover": {
      backgroundColor: "rgba(62, 131, 250, 0.12)",
    },
    "& .MuiListItemIcon-root": { minWidth: "35px" },
    "& .MuiListItemText-primary": {
      fontFamily: theme.fontFamily.MARKPROMEDIUM,
      fontSize: "15px",
    },
  },
  // When any child of the group is active, tint the header so the user can
  // still see at a glance which section they're in even if the group is closed.
  groupHeaderActive: {
    color: theme.colors.DodgerBlue,
    "& .MuiListItemText-primary": { fontFamily: theme.fontFamily.MARKPROBOLD },
  },
  groupCaret: {
    color: theme.colors.DodgerBlueLight,
    fontSize: "22px",
    transition: "transform 0.15s ease",
  },
  childList: {
    padding: "2px 0 6px",
  },
  childTab: {
    margin: "2px 12px 2px 28px",
    borderRadius: "10px",
    paddingLeft: "16px",
    transition: "all 0.2s ease",
    color: theme.colors.DodgerBlueLight,
    "&:hover": {
      backgroundColor: "rgba(62, 131, 250, 0.10)",
    },
    "&.Mui-selected": {
      backgroundColor: theme.colors.DodgerBlueLight,
      color: theme.colors.White,
      boxShadow: "0px 4px 12px rgba(62, 131, 250, 0.22)",
    },
    "& .MuiListItemIcon-root": { minWidth: "30px" },
    "& .MuiListItemText-primary": {
      fontFamily: theme.fontFamily.MARKPROMEDIUM,
      fontSize: "14px",
    },
  },
  soonText: {
    textTransform: "uppercase",
    position: "absolute",
    backgroundColor: theme.colors.Violet,
    color: theme.colors.MintGreen,
    padding: "1px 2px",
    left: 130,
    top: 15,
    fontSize: "7px",
    borderRadius: "2px",
    fontFamily: theme.fontFamily.MARKPROBOLD,
    opacity: 1,
  },
}));

// A single navigable tab (leaf). Used both at top level and inside a group.
function TabRow({ tab, activeTab, baseURL, classes, variant }) {
  const isChild = variant === "child";
  return (
    <ListItem
      button
      className={isChild ? classes.childTab : classes.tab}
      selected={activeTab === tab.pathName}
      onClick={() => history.push(`${baseURL}/${tab.pathName}`)}
      disabled={!tab.isActive}
    >
      <ListItemIcon>
        <img
          src={`/images/${tab.icon}`}
          alt={tab.icon}
          style={{
            filter: activeTab === tab.pathName ? "brightness(0) invert(1)" : "",
            height: isChild ? "20px" : "25px",
            width: isChild ? "20px" : "25px",
          }}
        />
      </ListItemIcon>
      <ListItemText
        primary={tab.name}
        style={{ opacity: tab.comingSoon ? 0.24 : 1 }}
      />
      {tab.comingSoon ? <span className={classes.soonText}>Soon</span> : null}
    </ListItem>
  );
}

// A tab/child is visible unless it declares `show: false` (or a `show` function
// that returns false). Omitting `show` keeps the item visible, so existing flat
// and grouped tab arrays keep working unchanged.
const isShown = (item) => {
  const cond = item?.show;
  if (typeof cond === "function") return cond() !== false;
  return cond !== false;
};

function Sidebar({ baseURL, tabs, activeTab }) {
  const classes = useStyles();

  // Track expanded groups by their pathName (used as a stable key). The set
  // persists across re-renders so the user's manual toggles survive prop
  // updates, but groups auto-open when their active child changes.
  const [expanded, setExpanded] = useState({});

  // Auto-expand whichever group contains the active tab so a deep-link or a
  // route change reveals the nested item immediately.
  const groupOwningActiveTab = useMemo(() => {
    for (const tab of tabs) {
      if (
        tab?.children?.length &&
        tab.children.filter(isShown).some((c) => c.pathName === activeTab)
      ) {
        return tab.pathName;
      }
    }
    return null;
  }, [tabs, activeTab]);

  useEffect(() => {
    if (groupOwningActiveTab && !expanded[groupOwningActiveTab]) {
      setExpanded((prev) => ({ ...prev, [groupOwningActiveTab]: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupOwningActiveTab]);

  const toggleGroup = (key) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <div className={classes.root}>
      <List className={classes.list}>
        {tabs.filter(isShown).map((each, index) => {
          // Group: parent header + collapsible child list. Recognised by the
          // presence of a non-empty children array — keeps the prop shape
          // backward-compatible with flat tab arrays.
          if (each?.children?.length) {
            // Drop hidden children; if none remain, hide the whole group.
            const childItems = each.children.filter(isShown);
            if (!childItems.length) return null;
            const isOpen = !!expanded[each.pathName];
            const ownsActive = groupOwningActiveTab === each.pathName;
            return (
              <React.Fragment key={`group-${each.pathName || index}`}>
                <ListItem
                  button
                  className={`${classes.groupHeader} ${ownsActive ? classes.groupHeaderActive : ""}`}
                  onClick={() => toggleGroup(each.pathName)}
                  disabled={each.isActive === false}
                >
                  <ListItemIcon>
                    <img
                      src={`/images/${each.icon}`}
                      alt={each.icon}
                      style={{ height: "25px", width: "25px" }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={each.name}
                    style={{ opacity: each.comingSoon ? 0.24 : 1 }}
                  />
                  {isOpen ? (
                    <ExpandMore className={classes.groupCaret} />
                  ) : (
                    <ChevronRight className={classes.groupCaret} />
                  )}
                </ListItem>
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding className={classes.childList}>
                    {childItems.map((child, childIndex) => (
                      <TabRow
                        key={`child-${child.pathName || childIndex}`}
                        tab={child}
                        activeTab={activeTab}
                        baseURL={baseURL}
                        classes={classes}
                        variant="child"
                      />
                    ))}
                  </List>
                </Collapse>
              </React.Fragment>
            );
          }

          return (
            <TabRow
              key={`tab-${each.pathName || index}`}
              tab={each}
              activeTab={activeTab}
              baseURL={baseURL}
              classes={classes}
              variant="top"
            />
          );
        })}
      </List>
    </div>
  );
}

export default Sidebar;
